import { ExploreOccupant, ExplorePeek, ExploreRoom, ExploreTemplate, parseTags } from '../../services/api/exploreService';
import './PeekSheet.css';

const CATEGORY_GLYPH: Record<string, string> = {
  games: '🎮',
  lobbies: '🛋️',
  interests: '🎯',
  dating: '💘',
};

const GAME_KIND_LABEL: Record<string, string> = {
  party: '🎉 Party',
  hunt: '🔍 Hunt',
  maze: '🌀 Maze',
  escape: '🗝️ Escape',
  race: '🏁 Race',
  quiz: '❓ Quiz',
  rts: '⚔️ Strategy',
};

function glyphFor(category?: string | null): string {
  return CATEGORY_GLYPH[(category ?? 'lobbies').toLowerCase()] ?? '🌐';
}

function initialOf(name: string): string {
  return (name?.trim()?.charAt(0) || '?').toUpperCase();
}

/**
 * "Best with N+ players" hint (design §3 R5). For a live room, `currentHumans`
 * turns it into an actionable count ("2 more to start") and the hint retires
 * itself once the threshold is met — a template (not yet live) has no current
 * headcount, so it stays a flat recommendation.
 */
function minPlayersHint(minPlayers?: number, currentHumans?: number): string | null {
  if (!minPlayers || minPlayers <= 1) return null;
  if (currentHumans !== undefined) {
    if (currentHumans >= minPlayers) return null;
    return `👥 Best with ${minPlayers}+ players — ${minPlayers - currentHumans} more to start`;
  }
  return `👥 Best with ${minPlayers}+ players`;
}

export type PeekTarget =
  | { kind: 'room'; room: ExploreRoom }
  | { kind: 'template'; template: ExploreTemplate };

interface PeekSheetProps {
  target: PeekTarget;
  /** Room roster (room targets only); null while loading or unavailable. */
  peek: ExplorePeek | null;
  peekLoading: boolean;
  joining: boolean;
  /** Resolved by the caller from the live SignalR room (the peek/explore projection
   * carries no host field) — same pattern RoomStoryCard uses for the classroom skin.
   * Room targets only. */
  hostName?: string | null;
  onClose: () => void;
  onJoin: (room: ExploreRoom) => void;
  onOpenWorld: (template: ExploreTemplate) => void;
}

export function PeekSheet({ target, peek, peekLoading, joining, hostName, onClose, onJoin, onOpenWorld }: PeekSheetProps) {
  const isRoom = target.kind === 'room';
  const category = isRoom ? target.room.category : target.template.category;
  const name = isRoom ? target.room.name : target.template.name;
  const blurb = isRoom
    ? target.room.blurb
    : target.template.blurb || target.template.description;
  const gameKind = isRoom ? target.room.gameKind : target.template.gameKind;
  const thumb = isRoom ? target.room.thumbnailUrl : target.template.thumbnailUrl;
  const tags = isRoom ? [] : parseTags(target.template.tags);

  const players = peek?.players ?? [];
  const humanCount = isRoom ? target.room.humanCount : 0;
  const maxPlayers = isRoom ? target.room.maxPlayers : target.template.minPlayers ?? 0;
  // hostName comes from the un-gated SignalR room (no peek-visibility check) — it must
  // never be shown on its own. Only reveal it once the gated roster independently shows
  // the same real name (i.e. the server already decided this viewer is entitled to it);
  // otherwise a "initials-only" host would leak their real name via this side channel
  // while their own roster row still correctly shows initials.
  const hostEntitled = players.some((p) => !p.isNpc && p.displayName === hostName);
  const showHost = isRoom && humanCount > 0 && !!hostName && hostEntitled;
  const minPlayersLine = isRoom
    ? minPlayersHint(peek?.template?.minPlayers, humanCount)
    : minPlayersHint(target.template.minPlayers);

  return (
    <div className="peek-backdrop" onClick={onClose}>
      <aside
        className="peek-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Peek at ${name}`}
      >
        <div className="peek-grabber" aria-hidden="true" />
        <button className="peek-close" onClick={onClose} aria-label="Close">×</button>

        <div className="peek-hero">
          {thumb ? (
            <img className="peek-thumb" src={thumb} alt="" />
          ) : (
            <span className={`peek-thumb peek-thumb--fallback cat-${(category ?? 'lobbies').toLowerCase()}`} aria-hidden="true">
              {glyphFor(category)}
            </span>
          )}
          <div className="peek-heading">
            <h2 className="peek-title">{name}</h2>
            <div className="peek-chips">
              {category && <span className={`peek-chip cat-${category.toLowerCase()}`}>{category}</span>}
              {gameKind && GAME_KIND_LABEL[gameKind.toLowerCase()] && (
                <span className="peek-chip kind">{GAME_KIND_LABEL[gameKind.toLowerCase()]}</span>
              )}
            </div>
          </div>
        </div>

        {blurb && <p className="peek-blurb">{blurb}</p>}

        {tags.length > 0 && (
          <div className="peek-tags">
            {tags.map((t) => (
              <span key={t} className="peek-tag">{t}</span>
            ))}
          </div>
        )}

        {showHost && (
          <p className="peek-host">🎪 Hosted by <strong>{hostName}</strong></p>
        )}

        {isRoom ? (
          <div className="peek-roster">
            <div className="peek-roster-head">
              <span className="peek-roster-title">Who's inside</span>
              <span className="peek-count">{humanCount}/{maxPlayers}</span>
            </div>
            {minPlayersLine && <p className="peek-minplayers">{minPlayersLine}</p>}
            {peekLoading ? (
              <div className="peek-roster-loading">
                <span className="peek-skeleton-row" />
                <span className="peek-skeleton-row" />
                <span className="peek-skeleton-row" />
              </div>
            ) : players.length > 0 ? (
              <div className="peek-roster-list">
                {players.map((p, i) => (
                  <OccupantRow key={`${p.displayName}-${i}`} occupant={p} />
                ))}
              </div>
            ) : (
              <p className="peek-empty">Nobody's here yet — be the mystery.</p>
            )}
          </div>
        ) : (
          minPlayersLine && <p className="peek-minplayers">{minPlayersLine}</p>
        )}

        <div className="peek-actions">
          {isRoom ? (
            <button
              className="peek-cta primary"
              disabled={joining}
              onClick={() => onJoin(target.room)}
            >
              {joining ? 'Joining…' : target.room.requiresApproval ? '🚪 Request to Join' : target.room.hasPassword ? '🔒 Join Room' : 'Join'}
            </button>
          ) : (
            <button
              className="peek-cta primary"
              disabled={joining}
              onClick={() => onOpenWorld(target.template)}
            >
              ✨ Open this world
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

/** One roster row: photo-chip (falls back to initials), NPC "AI" tag, shared-interest
 * highlight. sharedInterestCount is only ever non-null for entitled, non-self, non-NPC
 * occupants (server-gated — see ExploreOccupant's doc), so the `!p.isNpc` guard here is
 * belt-and-suspenders, not the actual enforcement point. */
function OccupantRow({ occupant: p }: { occupant: ExploreOccupant }) {
  const shared = !p.isNpc && (p.sharedInterestCount ?? 0) > 0;
  return (
    <div className="peek-roster-row">
      <span className={`peek-roster-avatar ${p.isNpc ? 'npc' : ''}`}>
        {p.photoUrl ? (
          <img className="peek-roster-avatar-photo" src={p.photoUrl} alt="" />
        ) : (
          <span className="peek-roster-avatar-initial">{initialOf(p.displayName)}</span>
        )}
      </span>
      <span className="peek-roster-row-info">
        <span className="peek-roster-row-name">
          {p.displayName}
          {p.isNpc && <span className="peek-roster-ai-tag" aria-label="Local character">AI</span>}
        </span>
        {shared && (
          <span className="peek-roster-shared">
            ✨ {p.sharedInterestCount} shared interest{p.sharedInterestCount === 1 ? '' : 's'}
          </span>
        )}
      </span>
    </div>
  );
}
